import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Handshake, Sliders, ProhibitInset, UserCircle, Star, Lightning } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/BottomNav';
import { toast } from 'sonner';
import { useEntitlement, useSmartNudges } from '../hooks/useEntitlement';
import { UpgradeChip, SmartNudgeStack } from '../components/UpgradePrompts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COMPETITIVENESS_OPTIONS = ['casual', 'moderate', 'competitive'];
const STRICTNESS_OPTIONS = ['relaxed', 'standard', 'strict'];
const ENERGY_OPTIONS = ['quiet', 'moderate', 'social'];
const GROUP_SIZE_OPTIONS = ['small', 'medium', 'large'];

export default function MatchmakingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('suggestions');
  const [prefs, setPrefs] = useState({});
  const [suggestedSessions, setSuggestedSessions] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const enhancedMatch = useEntitlement('matchmaking.enhanced');
  const { nudges, dismiss: dismissNudge } = useSmartNudges();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [prefsRes, sessRes, blockedRes] = await Promise.all([
        axios.get(`${API_URL}/api/matchmaking/preferences`, { withCredentials: true }),
        axios.get(`${API_URL}/api/matchmaking/suggested-sessions`, { withCredentials: true }),
        axios.get(`${API_URL}/api/matchmaking/blocked`, { withCredentials: true }),
      ]);
      setPrefs(prefsRes.data);
      setSuggestedSessions(sessRes.data);
      setBlockedUsers(blockedRes.data);
    } catch (err) { /* silent */ }
    finally { setLoading(false); }
  };

  const savePrefs = async () => {
    try {
      await axios.put(`${API_URL}/api/matchmaking/preferences`, prefs, { withCredentials: true });
      toast.success('Preferences saved!');
    } catch (err) { toast.error('Failed to save'); }
  };

  const unblockUser = async (userId) => {
    try {
      await axios.delete(`${API_URL}/api/matchmaking/block/${userId}`, { withCredentials: true });
      setBlockedUsers(prev => prev.filter(b => b.blocked_user_id !== userId));
      toast.success('User unblocked');
    } catch (err) { toast.error('Failed to unblock'); }
  };

  const OptionSelector = ({ label, value, options, onChange }) => (
    <div className="mb-4">
      <label className="text-[#A1A1AA] text-xs mb-2 block">{label}</label>
      <div className="flex gap-2">
        {options.map(opt => (
          <button key={opt} onClick={() => onChange(opt)}
            className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all ${
              value === opt ? 'bg-[#9D4CDD]/20 text-[#9D4CDD] border border-[#9D4CDD]/30' : 'bg-[#181824] text-[#71717A] border border-transparent'
            }`}>
            {opt.charAt(0).toUpperCase() + opt.slice(1)}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="app-container" data-testid="matchmaking-page">
      <header className="p-6 pb-3">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-[#181824] flex items-center justify-center">
            <ArrowLeft size={20} weight="bold" className="text-white" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              <Handshake size={24} weight="duotone" className="text-[#00F0FF]" />
              Matchmaking
            </h1>
            <p className="text-[#71717A] text-xs">Find your perfect game group</p>
          </div>
        </div>

        <div className="flex gap-2">
          {['suggestions', 'preferences', 'blocked'].map(t => (
            <button key={t} onClick={() => setActiveTab(t)}
              className={`flex-1 py-2 rounded-xl text-xs font-medium transition-all ${activeTab === t ? 'bg-gradient-to-br from-[#9D4CDD] to-[#7C3AED] text-white' : 'bg-[#181824] text-[#A1A1AA]'}`}>
              {t === 'suggestions' ? 'For You' : t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
      </header>

      <div className="px-6 pb-32">
        {/* Smart Nudges for matchmaking */}
        <SmartNudgeStack nudges={nudges.filter(n => n.feature_highlight?.includes('matchmaking'))} maxShow={1} onDismiss={dismissNudge} />

        {activeTab === 'suggestions' && (
          <div className="space-y-3">
            {enhancedMatch.allowed ? (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#00F0FF]/[0.05] border border-[#00F0FF]/15 mb-2">
                <Lightning size={14} weight="fill" className="text-[#00F0FF]" />
                <span className="text-[11px] text-[#00F0FF] font-medium">Enhanced Matchmaking Active</span>
              </div>
            ) : (
              <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-white/[0.02] border border-white/[0.05] mb-2" data-testid="matchmaking-upgrade-hint">
                <span className="text-[11px] text-[#555]">Better matches with advanced filters</span>
                <UpgradeChip plan="Plus" label="Unlock" />
              </div>
            )}
            <p className="text-[#A1A1AA] text-xs mb-2">Recommended sessions based on your profile</p>
            {loading ? (
              [1,2,3].map(i => <div key={i} className="h-20 bg-[#181824] rounded-xl animate-pulse" />)
            ) : suggestedSessions.length > 0 ? (
              suggestedSessions.map((s, i) => (
                <motion.div key={s.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                  onClick={() => navigate(`/events/${s.id}`)}
                  className="p-4 rounded-xl bg-[#0F0F16] border border-white/5 cursor-pointer hover:border-[#9D4CDD]/30 transition-all">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-white text-sm font-medium">{s.title}</h3>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#00F0FF]/10 text-[#00F0FF]">{s.game_type}</span>
                  </div>
                  <div className="flex items-center gap-3 text-[10px] text-[#71717A]">
                    <span>{s.date}</span>
                    <span>{s.location}</span>
                    <span>{s.current_players}/{s.max_players} players</span>
                  </div>
                  {s.match_score > 0 && (
                    <div className="mt-2 flex items-center gap-1">
                      <Star size={12} weight="fill" className="text-[#FACC15]" />
                      <span className="text-[#FACC15] text-[10px] font-medium">Match Score: {s.match_score}%</span>
                    </div>
                  )}
                </motion.div>
              ))
            ) : (
              <div className="text-center py-8 text-[#71717A] text-sm">
                <p>No suggested sessions right now</p>
                <p className="text-xs mt-1">Create a session or update your preferences</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'preferences' && (
          <div className="mt-3">
            <OptionSelector label="Competitiveness" value={prefs.preferred_competitiveness} options={COMPETITIVENESS_OPTIONS}
              onChange={v => setPrefs(p => ({ ...p, preferred_competitiveness: v }))} />
            <OptionSelector label="Rules Strictness" value={prefs.rules_strictness} options={STRICTNESS_OPTIONS}
              onChange={v => setPrefs(p => ({ ...p, rules_strictness: v }))} />
            <OptionSelector label="Social Energy" value={prefs.social_energy} options={ENERGY_OPTIONS}
              onChange={v => setPrefs(p => ({ ...p, social_energy: v }))} />
            <OptionSelector label="Group Size" value={prefs.preferred_group_size} options={GROUP_SIZE_OPTIONS}
              onChange={v => setPrefs(p => ({ ...p, preferred_group_size: v }))} />

            <div className="flex gap-4 mb-6">
              <label className="flex items-center gap-2 text-xs text-[#A1A1AA]">
                <input type="checkbox" checked={prefs.family_friendly_only || false}
                  onChange={e => setPrefs(p => ({ ...p, family_friendly_only: e.target.checked }))}
                  className="rounded border-[#71717A]" />
                Family Friendly Only
              </label>
              <label className="flex items-center gap-2 text-xs text-[#A1A1AA]">
                <input type="checkbox" checked={prefs.alcohol_friendly !== false}
                  onChange={e => setPrefs(p => ({ ...p, alcohol_friendly: e.target.checked }))}
                  className="rounded border-[#71717A]" />
                Alcohol Friendly
              </label>
            </div>

            <button onClick={savePrefs} className="btn-primary w-full" data-testid="save-preferences-btn">Save Preferences</button>
          </div>
        )}

        {activeTab === 'blocked' && (
          <div className="space-y-2 mt-3">
            {blockedUsers.length > 0 ? (
              blockedUsers.map(b => (
                <div key={b.blocked_user_id} className="p-3 rounded-xl bg-[#0F0F16] border border-white/5 flex items-center gap-3">
                  <UserCircle size={32} weight="duotone" className="text-[#71717A]" />
                  <div className="flex-1">
                    <p className="text-white text-sm">{b.name}</p>
                    <p className="text-[#71717A] text-[10px]">@{b.username}</p>
                  </div>
                  <button onClick={() => unblockUser(b.blocked_user_id)}
                    className="px-3 py-1 rounded-lg bg-[#EF4444]/10 text-[#EF4444] text-xs hover:bg-[#EF4444]/20 transition-all">
                    Unblock
                  </button>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-[#71717A] text-sm">
                <ProhibitInset size={32} className="mx-auto mb-2 opacity-50" />
                <p>No blocked users</p>
              </div>
            )}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
